import React, { useState } from 'react';
import Select from 'react-select';
import Form from './Form';
import styles from './IndustrySelectionForm.module.css';

interface IndustrySelectionFormProps {
  formId: string;
  title: string;
  description: string;
  industryGroups: string[];
  experienceOptions: number[];
  onSubmit: (formData: { industryGroup: string; yearsOfExperience: number }) => void;
}

interface OptionType {
  value: string | number;
  label: string;
}

const IndustrySelectionForm: React.FC<IndustrySelectionFormProps> = ({
  formId,
  title,
  description,
  industryGroups,
  experienceOptions,
  onSubmit
}) => {
  const [selectedIndustry, setSelectedIndustry] = useState<OptionType | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<OptionType | null>(null);
  const [errors, setErrors] = useState<{ industry?: string; experience?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create options for react-select
  const industryOptions: OptionType[] = industryGroups.map(industry => ({
    value: industry,
    label: industry
  }));

  const experienceOptionsList: OptionType[] = experienceOptions.map(years => ({
    value: years,
    label: years === 1 ? '1 year' : `${years} years`
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: { industry?: string; experience?: string } = {};
    
    if (!selectedIndustry) {
      newErrors.industry = 'Please select your target industry';
    }
    
    if (!selectedExperience) {
      newErrors.experience = 'Please select your years of experience';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0 && selectedIndustry && selectedExperience) {
      setIsSubmitting(true);
      try {
        await onSubmit({
          industryGroup: selectedIndustry.value as string,
          yearsOfExperience: selectedExperience.value as number
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleIndustryChange = (newValue: OptionType | null) => {
    setSelectedIndustry(newValue);
    if (errors.industry) {
      setErrors(prev => ({ ...prev, industry: undefined }));
    }
  };

  const handleExperienceChange = (newValue: OptionType | null) => {
    setSelectedExperience(newValue);
    if (errors.experience) {
      setErrors(prev => ({ ...prev, experience: undefined }));
    }
  };

  // Custom styles for react-select to match our theme
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'var(--color-input-background)',
      border: `2px solid ${state.isFocused ? 'var(--color-primary)' : 'var(--color-border-primary)'}`,
      borderRadius: 'var(--radius-md)',
      minHeight: '48px',
      boxShadow: state.isFocused 
        ? '0 0 0 3px rgba(59, 130, 246, 0.1), 0 2px 8px rgba(59, 130, 246, 0.15)'
        : 'none',
      transition: 'all var(--transition-fast)',
      '&:hover': {
        borderColor: state.isFocused ? 'var(--color-primary)' : 'var(--color-border-secondary)',
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'var(--color-primary)'
        : state.isFocused 
        ? 'var(--color-neutral-100)'
        : 'var(--color-background)',
      color: state.isSelected 
        ? 'var(--color-text-inverse)'
        : 'var(--color-text-primary)',
      padding: 'var(--space-3) var(--space-4)',
      '&:hover': {
        backgroundColor: state.isSelected 
          ? 'var(--color-primary)'
          : 'var(--color-neutral-100)',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-background)',
      border: '1px solid var(--color-border-primary)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-lg)',
      zIndex: 9999,
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: 0,
      maxHeight: '200px',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-tertiary)',
      fontSize: 'var(--font-size-base)',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-base)',
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-base)',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-border-primary)',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-secondary)',
      '&:hover': {
        color: 'var(--color-text-primary)',
      },
    }),
  };

  const industryIcon = (
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Paper and pen icon">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4z" fill="#3b82f6"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25z" fill="#3b82f6"/>
</svg>
  );

  return (
    <Form
      title={title}
      description={description}
      icon={industryIcon}
      isLoading={isSubmitting}
      onSubmit={handleSubmit}
      submitButtonText="Continue"
    >
      <div className={styles.formFields}>
        <div className={styles.fieldContainer}>
          <label className={styles.fieldLabel}>
            Target Industry <span className={styles.required}>*</span>
          </label>
          <Select
            options={industryOptions}
            value={selectedIndustry}
            onChange={handleIndustryChange}
            placeholder="Select your target industry..."
            isSearchable
            isClearable
            styles={selectStyles}
            className={`${styles.selectContainer} ${errors.industry ? styles.selectError : ''}`}
            classNamePrefix="industry-select"
            isDisabled={isSubmitting}
            noOptionsMessage={({ inputValue }) => 
              inputValue ? `No industries found matching "${inputValue}"` : 'No industries available'
            }
            filterOption={(option, inputValue) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
          {errors.industry && (
            <span className={styles.errorMessage}>{errors.industry}</span>
          )}
        </div>

        <div className={styles.fieldContainer}>
          <label className={styles.fieldLabel}>
            Years of Experience <span className={styles.required}>*</span>
          </label>
          <Select
            options={experienceOptionsList}
            value={selectedExperience}
            onChange={handleExperienceChange}
            placeholder="Select your years of experience..."
            isSearchable
            isClearable
            styles={selectStyles}
            className={`${styles.selectContainer} ${errors.experience ? styles.selectError : ''}`}
            classNamePrefix="experience-select"
            isDisabled={isSubmitting}
            noOptionsMessage={({ inputValue }) => 
              inputValue ? `No experience options found matching "${inputValue}"` : 'No experience options available'
            }
            filterOption={(option, inputValue) => {
              const searchValue = inputValue.toLowerCase();
              const optionLabel = option.label.toLowerCase();
              const optionValue = option.value.toString();
              return optionLabel.includes(searchValue) || optionValue.includes(searchValue);
            }}
          />
          {errors.experience && (
            <span className={styles.errorMessage}>{errors.experience}</span>
          )}
        </div>
      </div>
    </Form>
  );
};

export default IndustrySelectionForm;